import {
  PricingAudienceFilter,
  type PricingFeatureCategory,
  type PricingFeatureItem,
  type PricingFeatureTiers,
} from './types';

const tierRegex = /\b(free|plus|premium)\b/i; // this should be overrideable from the Contentful options

export function createFeatureKey(key: string): string {
  return key.replaceAll(' ', '-').toLowerCase();
}

function getTierFormattedResult(rawMatches: { [key: string]: any }[], tiersKey: string): PricingFeatureTiers {
  const tierResult: PricingFeatureTiers = {
    tier_free: false,
    tier_plus: false,
    tier_premium: false,
  };

  if (rawMatches.length === 0) {
    return tierResult;
  }

  // if only a single object, then assume that it or child object of key `tiersKey`contains the tiers
  if (rawMatches.length === 1) {
    let obj = rawMatches[0];
    // if the object has a tiersKey, then use that object
    if (tiersKey && obj[tiersKey]) {
      obj = obj[tiersKey];
    }

    // find the tierResult key that matches the tier
    for (const key in obj) {
      if (typeof obj[key] === 'string') {
        const match = key.match(tierRegex);
        if (match) {
          const tierKey = `tier_${match[0].toLowerCase()}`;
          tierResult[tierKey as keyof typeof tierResult] = obj[key];
        }
      }
    }
  }

  // if multiple objects, then assume that each object contains the tiers
  else {
    for (const obj of rawMatches) {
      if (tiersKey && obj[tiersKey] && typeof obj[tiersKey] === 'string' && obj['_tier']) {
        const tierKey = `tier_${obj['_tier']}`;
        tierResult[tierKey as keyof typeof tierResult] = obj[tiersKey];
      }
    }
  }

  return tierResult;
}

export function getTiersForAdditionalKeys(dataSource: any, searchValue: string) {
  const [objectKey, tiersKey] = searchValue.split('.');
  const rawMatches: { [key: string]: any }[] = [];

  function searchObject(obj: { [key: string]: any }, parentTier: string | null = null) {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }

    // detect tiers
    let tier = parentTier; // pass down the tier from the parent object
    for (const key in obj) {
      if (typeof obj[key] === 'string') {
        const match = obj[key].match(tierRegex);
        if (match) {
          tier = match[0].toLowerCase();
        }
      }
    }

    // check if key or value matches the object key
    for (const key in obj) {
      let match: { [key: string]: any } | null = null;

      // first check if the key matches the search value
      if (key === objectKey) {
        // if the value is an object, return the object
        if (typeof obj[key] === 'object') {
          match = { ...obj[key] };
        }
        // otherwise return the containing object
        else {
          match = { ...obj };
        }
      }

      // then check if the value matches the search value
      else if (obj[key] === objectKey) {
        match = { ...obj };
      }

      // if a match is found, add the tier and push to the rawMatches array
      if (match) {
        if (tier) {
          match['_tier'] = tier; // add tier if found
        }
        rawMatches.push(match);
      } else {
        searchObject(obj[key], tier); // Recursive search in nested structures
      }
    }
  }

  searchObject(dataSource); // start the search
  return getTierFormattedResult(rawMatches, tiersKey); // format the results
}

// ensure all features have a unique key (prevent dupes)
export function getUniqueFeatures(features: PricingFeatureItem[]): PricingFeatureItem[] {
  const usedKeys: string[] = [];
  return features.filter((feature) => {
    const key = feature.key || '';
    if (usedKeys.includes(key)) {
      return false;
    }
    usedKeys.push(key);
    return true;
  });
}

// reformat the feature data to be consumed by the svelte template
/*
  // outputs the following format for flat (subcategory-only) tables
  [
    {
      name: "Sub cat A",
      key: 'sub-cat-a",
      features: [ ... ]
    }
  ]
*/
export function subcategorizeFeatures(
  features: PricingFeatureItem[],
  elevatedSubcategories: string[]
): PricingFeatureCategory[] {
  const result: PricingFeatureCategory[] = [];

  features.forEach((item) => {
    // Find or create category
    let subcategory = result.find((subcat) => subcat.key === item.subcategory_key);
    if (!subcategory) {
      subcategory = {
        name: item.subcategory,
        key: item.subcategory_key,
        features: [],
      };
      result.push(subcategory);
    }
    if (subcategory.features) {
      subcategory.features.push(item);
    }
  });

  // filter out any subcategories that weren't included
  const filteredResult = result.filter((subcat) => elevatedSubcategories.includes(subcat.key));
  return filteredResult;
}

// reformat the feature data to be consumed by the svelte template
/*
  // outputs the following format for nested (category+subcategory) tables
  [
    {
      name: "Category B",
      key: "category-b",
      subcategories: [
        {
          name: "Sub Category C",
          key": "category-b_sub-category-c",
          features: [ ... ]
        }
      ]
    }
  ]
*/
export function categorizeFeatures(features: PricingFeatureItem[]): PricingFeatureCategory[] {
  const result: PricingFeatureCategory[] = [];

  features.forEach((item) => {
    // Find or create category
    let category = result.find((c) => c.key === item.category_key);
    if (!category) {
      category = {
        name: item.category,
        key: item.category_key,
        subcategories: [],
      };
      result.push(category);
    }

    // Find or create subcategory
    if (category.subcategories) {
      let subcategory = category.subcategories.find((sc) => sc.key === item.subcategory_key);
      if (!subcategory) {
        subcategory = {
          name: item.subcategory,
          key: item.subcategory_key,
          features: [],
        };
        category.subcategories.push(subcategory);
      }

      // Add feature to subcategory
      if (subcategory.features) {
        subcategory.features.push(item);
      }
    }
  });

  return result;
}

export function reorderCategories(
  includedCategories: string[] | undefined,
  categorizedFeatures: PricingFeatureCategory[]
): PricingFeatureCategory[] {
  // if a category order was privided, reorder the categories
  if (includedCategories) {
    return categorizedFeatures
      .slice()
      .sort((a, b) => includedCategories.indexOf(a.key) - includedCategories.indexOf(b.key));
  }
  // otherwise return the same order as the data source
  return categorizedFeatures;
}

// replaces feature data with content from other sources via additional keys
export function updateTierValuesWithAdditionalKeyData(dataSource: any, feature: PricingFeatureItem) {
  const additionalKeys = feature.additional_keys;
  if (!additionalKeys || additionalKeys.length === 0) {
    return feature;
  }
  const key = additionalKeys[0];
  const tierValues = getTiersForAdditionalKeys(dataSource, key);
  return {
    ...feature,
    tier_free: tierValues.tier_free,
    tier_plus: tierValues.tier_plus,
    tier_premium: tierValues.tier_premium,
  };
}

function sortArray(arr: any[], field: string) {
  arr.sort((a, b) => {
    const valueA = a[field];
    const valueB = b[field];

    if (valueA === null && valueB === null) {
      return 0;
    } else if (valueA === null) {
      return -1; // nulls sort before numbers
    } else if (valueB === null) {
      return 1; // nulls sort before numbers
    } else {
      return valueA - valueB; // numeric sort
    }
  });
  return arr;
}

// gets the top features for an audience and sorts them by rank
export function getTopFeaturesForAudience(
  allFeatures: PricingFeatureItem[],
  audience: PricingAudienceFilter
): PricingFeatureItem[] {
  // map the audience keys to the top feature keys
  const audienceKeyMap: Record<PricingAudienceFilter, keyof PricingFeatureItem> = {
    [PricingAudienceFilter.All]: 'top_all',
    [PricingAudienceFilter.Restaurants]: 'top_restaurants',
    [PricingAudienceFilter.Retail]: 'top_retail',
    [PricingAudienceFilter.Beauty]: 'top_beauty',
    [PricingAudienceFilter.Services]: 'top_services',
  };

  // ensure the selected audience has top features
  const key = audienceKeyMap[audience];
  if (!key) {
    return [];
  }

  // sort the top features for the audience to an ascending list (top features have values of 1-4)
  return sortArray(
    allFeatures.filter((feature) => feature[key]),
    key
  );
}

/**
 * Filters feature objects based on matching filter properties with given categories
 *
 * @param features Array of feature filter objects
 * @param audiences Array of category strings (without 'filter_' prefix)
 * @returns Array of feature objects that have at least one matching true filter
 */
export function filterFeaturesByAudiences(features: PricingFeatureItem[], audiences: string[]): PricingFeatureItem[] {
  return features.filter((feature) =>
    // Check if any of the categories match with their corresponding filter_ property
    audiences.some((audience) => {
      const filterKey = `filter_${audience}` as keyof PricingFeatureItem;
      return feature[filterKey];
    })
  );
}
